import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check, PenLine } from "lucide-react";

/**
 * SignaturePad — finger / stylus / mouse signature canvas.
 *
 * Props:
 *  - value: optional existing data URL (PNG) to show as preview
 *  - onChange(dataUrl|null): called when user confirms ("Conferma") or clears
 *  - height: canvas px height (default 180)
 *  - disabled: if true, hide pad and show preview only
 */
export default function SignaturePad({
  value = "",
  onChange,
  height = 180,
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Initialize canvas with DPR scaling and white background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = "#0a1a3a";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      hasInkRef.current = false;
      setHasInk(false);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPoint(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPoint(e);
    const last = lastPtRef.current || p;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  const end = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    try {
      canvasRef.current.releasePointerCapture?.(e.pointerId);
    } catch {
      // pointer already released — safe to ignore
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    hasInkRef.current = false;
    setHasInk(false);
    if (onChange) onChange(null);
  };

  const confirm = () => {
    if (!hasInk) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    if (onChange) onChange(dataUrl);
  };

  // Preview mode (signature already saved)
  if (value && disabled) {
    return (
      <div className="border-2 border-green-200 bg-green-50 rounded-md p-3">
        <img
          src={value}
          alt="Firma"
          className="max-h-32 bg-white rounded mx-auto block"
          data-testid="firma-preview-img"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className="relative rounded-md border-2 border-dashed border-slate-300 bg-white overflow-hidden"
        style={{ height: `${height}px` }}
      >
        <canvas
          ref={canvasRef}
          data-testid="firma-canvas"
          className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-slate-400">
            <PenLine className="h-7 w-7" />
            <div className="text-xs font-bold mt-1 uppercase tracking-wider">
              Firma qui con il dito
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={clear}
          disabled={!hasInk}
          className="h-11"
          data-testid="firma-canvas-clear"
        >
          <Eraser className="h-4 w-4 mr-2" /> Cancella
        </Button>
        <Button
          type="button"
          onClick={confirm}
          disabled={!hasInk}
          className="h-11 bg-[#0044FF] hover:bg-[#0033CC]"
          data-testid="firma-canvas-confirm"
        >
          <Check className="h-4 w-4 mr-2" /> Conferma firma
        </Button>
      </div>
    </div>
  );
}
